import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";

import { OrganizationService } from "services/organization";
import { ReactQueryKeysGlobal } from "services/queryKeys";

import { StatusMessageType } from "components/StatusMessageQueue/StatusMessageQueueProvider";
import useStatusMessageQueue from "components/StatusMessageQueue/hooks/useStatusMessageQueue";

import { OrganizationSettings, setOrganizationSetting } from "./settings";

export const useOrganizationSettings = () => {
  const queryClient = useQueryClient();
  const { putStatusMessage } = useStatusMessageQueue();

  const { data: organizationSettings } = useSuspenseQuery({
    queryKey: [ReactQueryKeysGlobal.organizationSettings],
    queryFn: OrganizationService.getOrganizationSettings,

    refetchOnMount: false,
  });

  const { mutateAsync: setSetting, isPending: isSettingPending } = useMutation({
    mutationFn: async (params: {
      key: keyof OrganizationSettings;
      value: OrganizationSettings[keyof OrganizationSettings];
    }) => {
      await setOrganizationSetting(params.key, params.value);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ReactQueryKeysGlobal.organizationSettings],
      });
    },
    onError: (error: Error) => {
      putStatusMessage({
        type: StatusMessageType.Error,
        message: error.message,
      });
    },
  });

  return {
    organizationSettings,
    setSetting,
    isSettingPending,
  };
};
